@if (bracket) {
  @if (bracket.champion_team_name) {
    <div class="champion-banner">
      <mat-icon>emoji_events</mat-icon>
      <span>Champion: <b>{{ bracket.champion_team_name }}</b></span>
    </div>
  }

  <div class="bracket-scroll" (scroll)="onBracketScroll()">
    <div #bracketElement class="bracket" [class.bracket--no-seeds]="!showSeeds" [style.--round-count]="rounds.length">
      <svg class="bracket-connectors" aria-hidden="true" focusable="false">
        @for (path of connectorPaths; track path.key) {
          <path
            class="bracket-connector-path"
            [attr.d]="path.d"
            [attr.data-source-matchup-ids]="path.sourceMatchupIds.join(',')"
            [attr.data-destination-matchup-id]="path.destinationMatchupId"
            [attr.data-source-round]="path.sourceRound"
            [attr.data-destination-round]="path.destinationRound" />
        }
      </svg>

      @for (round of rounds; track $index; let r = $index) {
        <div class="round-col">
          <div class="round-title">
            <span>{{ roundLabel(round[0].round) }}</span>
          </div>
          <div class="round-matchups">
            @for (m of round; track m.id) {
              <div class="matchup" [attr.data-matchup-id]="m.id" [class.matchup--bye]="m.is_bye" [class.matchup--final]="m.winner_team_id" [class.matchup--live]="isLiveMatchup(m)">
                <!-- Date / time / court -->
                @if (hasSchedule(m) && schedulingMatchupId !== m.id) {
                  <div class="matchup-schedule matchup-schedule-top">
                    <mat-icon>event</mat-icon>
                    <span>{{ scheduleLabel(m) }}</span>
                  </div>
                }

                @if (isLiveMatchup(m)) {
                  <div class="matchup-live-state" aria-live="polite">
                    <span class="matchup-live-chip"><span class="matchup-live-dot"></span> Live</span>
                    <span class="matchup-live-period">{{ livePeriodLabel(m) }}</span>
                    <strong>{{ liveClock(m) }}</strong>
                  </div>
                }

                <!-- Home -->
                <div class="team-row" [class.team-row--winner]="isWinner(m, 'home')">
                  <span class="seed" *ngIf="showSeeds">{{ m.home_seed ?? '' }}</span>
                  <span class="team-flag">
                    @if (teamLogoUrl(m.home_team_id); as logo) {
                      <img class="team-logo" [src]="logo" alt="" (error)="markLogoBroken(m.home_team_id)" />
                    } @else if (flagImageUrl(m.home_team_name); as f) {
                      <img [src]="f" alt="" (error)="markFlagBroken(m.home_team_name)" />
                    } @else if (flagEmoji(m.home_team_name); as emoji) {
                      <span class="team-flag-emoji">{{ emoji }}</span>
                    } @else if (m.home_team_name) {
                      <span class="team-monogram">{{ monogram(m.home_team_name) }}</span>
                    }
                  </span>
                  <span class="team-name" [class.team-name--empty]="!m.home_team_name">
                    {{ m.home_team_name || (m.round >= 2 ? 'TBD' : (m.is_bye ? 'Bye' : 'TBD')) }}
                  </span>
                  @if (editingMatchupId === m.id) {
                    <input class="score-input" type="number" min="0" [(ngModel)]="editHome" />
                  } @else {
                    <span class="score">{{ m.home_score ?? '' }}</span>
                  }
                </div>

                <!-- Away -->
                <div class="team-row" [class.team-row--winner]="isWinner(m, 'away')">
                  <span class="seed" *ngIf="showSeeds">{{ m.away_seed ?? '' }}</span>
                  <span class="team-flag">
                    @if (teamLogoUrl(m.away_team_id); as logo) {
                      <img class="team-logo" [src]="logo" alt="" (error)="markLogoBroken(m.away_team_id)" />
                    } @else if (flagImageUrl(m.away_team_name); as f) {
                      <img [src]="f" alt="" (error)="markFlagBroken(m.away_team_name)" />
                    } @else if (flagEmoji(m.away_team_name); as emoji) {
                      <span class="team-flag-emoji">{{ emoji }}</span>
                    } @else if (m.away_team_name) {
                      <span class="team-monogram">{{ monogram(m.away_team_name) }}</span>
                    }
                  </span>
                  <span class="team-name" [class.team-name--empty]="!m.away_team_name">
                    {{ m.away_team_name || (m.round >= 2 ? 'TBD' : (m.is_bye ? 'Bye' : 'TBD')) }}
                  </span>
                  @if (editingMatchupId === m.id) {
                    <input class="score-input" type="number" min="0" [(ngModel)]="editAway" />
                  } @else {
                    <span class="score">{{ m.away_score ?? '' }}</span>
                  }
                </div>

                <!-- Venue location -->
                @if ((m.venue_school_name || m.venue_address) && schedulingMatchupId !== m.id) {
                  <div class="matchup-location">
                    <mat-icon>location_on</mat-icon>
                    <span>{{ m.venue_school_name || '' }}<ng-container *ngIf="m.venue_address"> · {{ m.venue_address }}</ng-container></span>
                  </div>
                }

                <!-- Date / court (editor) -->
                @if (editable && schedulingMatchupId === m.id) {
                  <div class="schedule-editor">
                    <div class="schedule-lock-note" *ngIf="isScheduleLocked(m)">
                      <mat-icon>lock</mat-icon>
                      <span>Final score is locked; date, time, and court can still be updated.</span>
                    </div>
                    <label>Date</label>
                    <input type="date" [(ngModel)]="schedDate" />
                    <label>Time</label>
                    <input type="time" [(ngModel)]="schedTime" />
                    <label>Court</label>
                    <select [(ngModel)]="schedCourt">
                      <option value="">— Select court —</option>
                      @for (gym of gymLocations; track gym.id) {
                        <option [value]="gym.court_name">{{ gym.court_name }}<ng-container *ngIf="gym.location_name"> · {{ gym.location_name }}</ng-container></option>
                      }
                    </select>
                    <div class="schedule-editor-actions">
                      <button mat-button class="mini-btn" (click)="submitSchedule(m)" [disabled]="savingMatchupId === m.id">
                        {{ savingMatchupId === m.id ? 'Saving…' : 'Save' }}
                      </button>
                      <button mat-button class="mini-btn ghost" (click)="cancelSchedule()">Cancel</button>
                    </div>
                  </div>
                }

                <!-- Actions -->
                @if (editable && (!m.is_bye || m.round >= 2)) {
                  <div class="matchup-actions">
                    @if (editingMatchupId === m.id) {
                      <button mat-button class="mini-btn" (click)="submitEdit(m)"
                              [disabled]="editHome === null || editAway === null || editHome === editAway || savingMatchupId === m.id">
                        {{ savingMatchupId === m.id ? 'Saving…' : 'Save' }}
                      </button>
                      <button mat-button class="mini-btn ghost" (click)="cancelEdit()">Cancel</button>
                    } @else if (schedulingMatchupId !== m.id) {
                      @if (canEditMatchup(m)) {
                        <button mat-button class="mini-btn ghost" (click)="startEdit(m)">
                          <mat-icon>edit</mat-icon> {{ m.winner_team_id ? 'Edit score' : 'Enter score' }}
                        </button>
                      }
                      @if (m.winner_team_id || m.home_score !== null || m.away_score !== null) {
                        <button mat-button class="mini-btn ghost" (click)="resetScore(m)">
                          <mat-icon>restart_alt</mat-icon> Reset score
                        </button>
                      }
                      <button mat-button class="mini-btn ghost" (click)="startSchedule(m)">
                        <mat-icon>event</mat-icon> {{ hasSchedule(m) ? 'Edit date' : 'Add date' }}
                      </button>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
}
